import { onStyleReady } from "./mapready";
import type { Map as MLMap } from "maplibre-gl";

/**
 * Base maps (grouped by provider in the map-type menu)
 *   streets          OpenStreetMap streets via OpenFreeMap (always available, free)
 *   maptiler_sat     MapTiler satellite imagery only                          — needs VITE_MAPTILER_KEY
 *   maptiler_hybrid  MapTiler satellite under our street names (hybrid)       — needs VITE_MAPTILER_KEY
 *   google_road      Google Maps roadmap (Map Tiles API, 2D)                   — needs VITE_GOOGLE_MAPS_KEY
 *   google_sat       Google satellite imagery only                             — needs VITE_GOOGLE_MAPS_KEY
 *   google_hybrid    Google satellite with Google roads + labels              — needs VITE_GOOGLE_MAPS_KEY
 * Which ones a user may pick = keys configured here ∩ organisations.map_providers of their companies
 * (Skyward staff get everything that has a key). Google is billed per tile, so it's opt-in per company.
 */
export const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_KEY as string | undefined)?.trim() || null;
export const GOOGLE_KEY = (import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined)?.trim() || null;
export const MAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";

export type Basemap = "streets" | "maptiler_sat" | "maptiler_hybrid" | "google_road" | "google_sat" | "google_hybrid";
export const ALL_BASEMAPS: Basemap[] = ["streets", "maptiler_hybrid", "maptiler_sat", "google_road", "google_hybrid", "google_sat"];
export const BASEMAP_LABEL: Record<Basemap, string> = {
  streets: "Streets", maptiler_sat: "Satellite", maptiler_hybrid: "Hybrid",
  google_road: "Google Maps", google_sat: "Google Satellite", google_hybrid: "Google Hybrid",
};
/** Short label inside a provider group of the menu. */
export const BASEMAP_SHORT: Record<Basemap, string> = {
  streets: "Streets", maptiler_sat: "Satellite", maptiler_hybrid: "Hybrid",
  google_road: "Map", google_sat: "Satellite", google_hybrid: "Hybrid",
};
export const BASEMAP_GROUPS: [string, Basemap[]][] = [
  ["OpenStreetMap", ["streets", "maptiler_hybrid", "maptiler_sat"]],
  ["Google", ["google_road", "google_hybrid", "google_sat"]],
];
/** Credit line for the footer when this base map is showing (Google shows its own mark on the map). */
export const BASEMAP_CREDIT: Partial<Record<Basemap, string>> = { maptiler_sat: "© MapTiler", maptiler_hybrid: "© MapTiler" };

/** organisations.map_providers values */
export const ORG_PROVIDERS = [
  ["maptiler", "Satellite & hybrid (MapTiler)"], ["google_roadmap", "Google Maps (roads)"], ["google_satellite", "Google satellite & hybrid"],
] as const;

export function availableBasemaps(orgProviders: string[] | "all"): Basemap[] {
  const has = (p: string) => orgProviders === "all" || orgProviders.includes(p);
  const out: Basemap[] = ["streets"];
  if (MAPTILER_KEY && has("maptiler")) out.push("maptiler_hybrid", "maptiler_sat");
  if (GOOGLE_KEY && has("google_roadmap")) out.push("google_road");
  if (GOOGLE_KEY && has("google_satellite")) out.push("google_hybrid", "google_sat");
  return out;
}

const PREF = "tf.basemap";
export function savedBasemap(allowed: Basemap[] = ALL_BASEMAPS): Basemap {
  let v: string | null = null;
  try { v = localStorage.getItem(PREF); } catch { /* storage blocked */ }
  if (v === "satellite") v = "maptiler_hybrid"; // older saved values: these were hybrids before V3.1.1
  try {
    if (v && !localStorage.getItem(PREF + ".v2")) {
      if (v === "maptiler_sat") v = "maptiler_hybrid";
      if (v === "google_sat") v = "google_hybrid";
      localStorage.setItem(PREF + ".v2", "1");
    }
  } catch { /* storage blocked */ }
  return allowed.includes(v as Basemap) ? (v as Basemap) : "streets";
}
export function saveBasemap(b: Basemap) {
  try { localStorage.setItem(PREF, b); localStorage.setItem(PREF + ".v2", "1"); } catch { /* ignore */ }
}

// ---------------------------------------------------------------- Google Map Tiles API sessions
type GSession = { session: string; expiry: number };
async function googleSession(kind: "roadmap" | "satellite" | "hybrid"): Promise<string> {
  const k = `tf.gsession.${kind}`;
  try {
    const c = JSON.parse(localStorage.getItem(k) ?? "null") as GSession | null;
    if (c && c.expiry * 1000 > Date.now() + 3600_000) return c.session;
  } catch { /* ignore */ }
  const body: Record<string, unknown> = { mapType: kind === "hybrid" ? "satellite" : kind, language: "en-GB", region: "FJ" };
  if (kind === "hybrid") body.layerTypes = ["layerRoadmap"]; // roads + labels on imagery
  const r = await fetch(`https://tile.googleapis.com/v1/createSession?key=${GOOGLE_KEY}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Google Maps session failed (${r.status})`);
  const j = (await r.json()) as { session: string; expiry: string };
  try { localStorage.setItem(k, JSON.stringify({ session: j.session, expiry: Number(j.expiry) })); } catch { /* ignore */ }
  return j.session;
}

const RASTER_IDS = ["bm-maptiler", "bm-maptiler-sat", "bm-google-road", "bm-google-sat", "bm-google-hybrid"];
const LAYER_FOR: Record<Basemap, string | null> = {
  streets: null, maptiler_hybrid: "bm-maptiler", maptiler_sat: "bm-maptiler-sat",
  google_road: "bm-google-road", google_sat: "bm-google-sat", google_hybrid: "bm-google-hybrid",
};

/** Put a raster layer either under the street labels (hybrid) or on top of the vector style, but below our own layers. */
function addRaster(map: MLMap, id: string, tiles: string, tileSize: number, attribution: string, under: "labels" | "top") {
  if (map.getLayer(id)) return;
  if (!map.getSource(id)) map.addSource(id, { type: "raster", tiles: [tiles], tileSize, maxzoom: 21, attribution });
  const layers = map.getStyle().layers;
  const ours = layers.find((l) => /^(hist-|zone-|tf-)/.test(l.id))?.id;
  const before = under === "labels" ? layers.find((l) => l.type === "symbol")?.id : ours;
  map.addLayer({ id, type: "raster", source: id, layout: { visibility: "none" } }, before);
}

/** Google requires the "Google Maps" mark and the viewport's data copyright on screen.
 *  V3.5.0: one timer per map (the main map and Follow windows no longer cancel each other), and the credit is a single
 *  compact line on small maps (full text in the tooltip) so it never covers a Follow window. */
function googleAttribution(map: MLMap, session: string | null) {
  const host = map.getContainer();
  let el = host.querySelector<HTMLDivElement>(".gattr");
  if (!session) { el?.remove(); map.off("moveend", (map as any).__gattr); return; }
  if (!el) { el = document.createElement("div"); el.className = "gattr"; host.appendChild(el); }
  const compact = () => el!.classList.toggle("compact", host.clientWidth < 560);
  const update = () => {
    compact();
    clearTimeout((map as any).__gattrT);
    (map as any).__gattrT = setTimeout(async () => {
      const b = map.getBounds();
      const q = new URLSearchParams({ session, key: GOOGLE_KEY!, zoom: String(Math.round(map.getZoom())),
        north: String(b.getNorth()), south: String(b.getSouth()), east: String(b.getEast()), west: String(b.getWest()) });
      try {
        const r = await fetch(`https://tile.googleapis.com/tile/v1/viewport?${q}`);
        const j = r.ok ? await r.json() : {};
        const c = String(j.copyright ?? "").replace(/[<>"]/g, "");
        el!.innerHTML = `<b aria-label="Google Maps">Google Maps</b><span title="${c}">${c}</span>`;
      } catch { el!.innerHTML = `<b aria-label="Google Maps">Google Maps</b>`; }
    }, 600);
  };
  map.off("moveend", (map as any).__gattr);
  map.off("resize", (map as any).__gattr);
  (map as any).__gattr = update;
  map.on("moveend", update);
  map.on("resize", compact);
  update();
}

/** Switch the visible base map. Safe to call before or after the style has loaded. */
export async function applyBasemap(map: MLMap, b: Basemap) {
  // Latest request wins: a slower earlier call (style still loading, Google session fetch) must not override it.
  const m = map as any;
  m.__wantBasemap = b;
  if (!map.isStyleLoaded() && !m.style?._loaded) {
    if (!m.__basemapWaiting) { m.__basemapWaiting = true; onStyleReady(map, () => { m.__basemapWaiting = false; applyBasemap(map, m.__wantBasemap); }); }
    return;
  }
  let gSession: string | null = null;
  const mtUrl = `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`;
  const mtAttr = '<a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener">© MapTiler</a>';
  try {
    if (b === "maptiler_hybrid" && MAPTILER_KEY) addRaster(map, "bm-maptiler", mtUrl, 512, mtAttr, "labels");
    if (b === "maptiler_sat" && MAPTILER_KEY) addRaster(map, "bm-maptiler-sat", mtUrl, 512, mtAttr, "top");
    if (b.startsWith("google") && GOOGLE_KEY) {
      gSession = await googleSession(b === "google_road" ? "roadmap" : b === "google_sat" ? "satellite" : "hybrid");
      const id = LAYER_FOR[b]!;
      if (map.getLayer(id) && (map as any)[`__${id}`] !== gSession) { map.removeLayer(id); map.removeSource(id); }
      addRaster(map, id, `https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${gSession}&key=${GOOGLE_KEY}`, 256, "", "top");
      (map as any)[`__${id}`] = gSession;
    }
  } catch (e) {
    console.warn("basemap", e);
    b = "streets";
  }
  if (m.__wantBasemap !== b && b !== "streets") return; // superseded while awaiting
  const want = LAYER_FOR[b];
  for (const id of RASTER_IDS) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", id === want ? "visible" : "none");
  googleAttribution(map, b.startsWith("google") ? gSession : null);
}
